import type { PerformanceDrilldownResponse } from "@/lib/api"

interface Props {
  data: PerformanceDrilldownResponse
  fmtCurrency: (v: number) => string
}

export function RevenueDrilldown({ data, fmtCurrency }: Props) {
  const bookings = data.bookings || []
  const channels = data.channel_revenue || []
  const avgBookingValue = bookings.length > 0
    ? bookings.reduce((sum, b) => sum + b.total_amount, 0) / bookings.length
    : 0

  return (
    <div>
      {/* Stat row */}
      <div className="drilldown-stat-row">
        <div className="drilldown-stat">
          <span className="drilldown-stat__label">Total revenue</span>
          <span className="drilldown-stat__value">{fmtCurrency(data.total_revenue || 0)}</span>
        </div>
        <div className="drilldown-stat">
          <span className="drilldown-stat__label">Bookings</span>
          <span className="drilldown-stat__value">{bookings.length}</span>
        </div>
        <div className="drilldown-stat">
          <span className="drilldown-stat__label">Avg booking</span>
          <span className="drilldown-stat__value">{fmtCurrency(avgBookingValue)}</span>
        </div>
      </div>

      {/* Channel revenue breakdown */}
      {channels.length > 0 && (
        <div className="drilldown-section">
          <h4 className="drilldown-section__title">Revenue by channel</h4>
          <div className="perf-channels">
            {channels.map(ch => (
              <div key={ch.name} className="perf-channel-row">
                <div className="perf-channel-row__info">
                  <span className="perf-channel-row__name">{ch.name}</span>
                </div>
                <div className="perf-channel-row__bar-wrap">
                  <div className="perf-channel-row__bar" style={{ width: `${Math.max(2, ch.pct)}%`, background: "var(--sf-primary)" }} />
                </div>
                <div className="perf-channel-row__stats">
                  <span className="perf-channel-row__pct">{ch.pct.toFixed(1)}%</span>
                  <span className="perf-channel-row__revenue">{fmtCurrency(ch.revenue)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bookings table */}
      {bookings.length > 0 && (
        <div className="drilldown-section">
          <h4 className="drilldown-section__title">Bookings</h4>
          <div className="perf-table-wrap">
            <table className="perf-table">
              <thead>
                <tr>
                  <th className="perf-table__th">Guest</th>
                  <th className="perf-table__th">Check-in</th>
                  <th className="perf-table__th perf-table__th--right">Nights</th>
                  <th className="perf-table__th">Channel</th>
                  <th className="perf-table__th perf-table__th--right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {bookings.slice(0, 30).map(b => (
                  <tr key={b.id}>
                    <td className="perf-table__td">{b.guest_name}</td>
                    <td className="perf-table__td">{b.check_in}</td>
                    <td className="perf-table__td perf-table__td--right">{b.nights}</td>
                    <td className="perf-table__td">{b.source || "Direct"}</td>
                    <td className="perf-table__td perf-table__td--right perf-table__td--mono">{fmtCurrency(b.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="drilldown-note">
        Revenue shown is gross booking amount. OTA commissions are not included.
      </div>
    </div>
  )
}
